import { polygonClippingEngine } from './polygonClippingEngine';
import type {
	ClipGeometry,
	ClipMultiPolygon,
	ClippingEngine,
	ClippingErrorReporter,
	ClippingOperation,
} from './types';

// State
// ----------------------------------------------------------------------------

/**
 * The active backend. Module-level because there is exactly one per process; the
 * setter exists so tests can inject a failing engine and so swapping libraries is
 * a one-line change rather than a rewrite.
 */
let engine: ClippingEngine = polygonClippingEngine;

/**
 * Where failures go.
 *
 * Defaults to a console warning rather than a no-op deliberately: the fallbacks
 * below are silent by design — a failed clip just measures more area — and a
 * quietly wrong BOM figure is worse than a loud one. `permissionUtils` warns the
 * same way for the same reason. An app can route this to its own logger.
 */
let report: ClippingErrorReporter = ({ engine: name, operation, error }) => {
	// oxlint-disable-next-line no-console
	console.warn(`[cutout] ${name}.${operation}() failed; treating the cut-out as absent.`, error);
};

// Methods
// ----------------------------------------------------------------------------

/**
 * Replace the polygon boolean backend.
 *
 * @param next The engine to use.
 * @returns A function restoring the previous engine, for test teardown.
 */
export const setClippingEngine = (next: ClippingEngine): (() => void) => {
	const previous = engine;
	engine = next;
	return () => {
		engine = previous;
	};
};

/**
 * Route clipping failures somewhere visible.
 *
 * Shared utils owns no logger and this runs in the browser, in Lambda and in
 * tests, so the app supplies one.
 *
 * @param next The reporter to call on failure.
 */
export const setClippingErrorReporter = (next: ClippingErrorReporter): void => {
	report = next;
};

/** The active engine's name, for diagnostics. */
export const clippingEngineName = (): string => engine.name;

/**
 * Run one operation, converting a throw into `null`.
 *
 * Every candidate library throws on some real-world geometry — the incumbent has
 * a decade of open "Unable to complete output ring" reports — and these run
 * inside a React render and a Pixi draw, where an escaping exception takes down
 * the whole take-off tool rather than one cut-out. Callers each choose a safe
 * meaning for `null`, and the rule is uniform: **behave as though the cut-out
 * were not there**, so a layer measures larger and over-orders material rather
 * than smaller.
 *
 * ! A hang is NOT covered. At least one published defect is an infinite loop, and
 * no try/catch helps with that; it would need a worker with a timeout.
 *
 * @param operation Which operation is running, for the failure report.
 * @param run Invokes the engine.
 * @returns The result, or `null` if the engine threw.
 */
const attempt = (operation: ClippingOperation, run: () => ClipMultiPolygon) => {
	try {
		return run();
	} catch (error) {
		report({ engine: engine.name, operation, error });
		return null;
	}
};

/**
 * Guarded polygon boolean operations. Each returns `null` when the engine fails.
 *
 * This is the only surface the rest of the cut-out code uses; nothing else
 * touches an engine directly.
 */
export const clip = {
	union: (first: ClipGeometry, ...rest: ClipGeometry[]): ClipMultiPolygon | null =>
		attempt('union', () => engine.union(first, ...rest)),

	intersection: (first: ClipGeometry, ...rest: ClipGeometry[]): ClipMultiPolygon | null =>
		attempt('intersection', () => engine.intersection(first, ...rest)),

	difference: (subject: ClipGeometry, ...clips: ClipGeometry[]): ClipMultiPolygon | null =>
		attempt('difference', () => engine.difference(subject, ...clips)),
};

export type {
	ClipGeometry,
	ClipMultiPolygon,
	ClipPolygon,
	ClipRing,
	ClippingEngine,
	ClippingErrorReporter,
	ClippingOperation,
} from './types';
